import React from "react";
import { Plus, Trash2 } from "lucide-react";
import ModernTimeDropdown from "../../layouts/modernDropdown.jsx";
import {
  MAX_PERIOD_COMPARISON_AREAS,
  PERIOD_COMPARISON_EMPLOYEE_FIELD,
  getPeriodComparisonAxisFields,
  PERIOD_COMPARISON_MEASURE_PLACEMENTS,
  PERIOD_COMPARISON_PERIODS,
  PERIOD_COMPARISON_TIMEZONE,
  PERIOD_COMPARISON_TYPES,
  isLegacyPeriodComparisonConfig,
  normalizePeriodComparisonConfig,
} from "./customReportVisualizationConfig.js";

const PERIOD_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  last_7_days: "Last 7 Days",
  last_30_days: "Last 30 Days",
};

const createAreaId = () =>
  `area_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const PeriodSelector = ({ periods, onChange }) => {
  const selectedPeriods = Array.isArray(periods) ? periods : [];
  const togglePeriod = (period) => {
    const isSelected = selectedPeriods.includes(period);
    const nextSelection = PERIOD_COMPARISON_PERIODS.filter((candidate) =>
      candidate === period
        ? !isSelected
        : selectedPeriods.includes(candidate)
    );
    if (nextSelection.length > 0) onChange(nextSelection);
  };

  return (
    <div className="rounded border border-gray-200 bg-white px-3 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
        Periods
      </div>
      <div
        className="mt-2 flex flex-wrap gap-2"
        role="group"
        aria-label="Comparison periods"
      >
        {PERIOD_COMPARISON_PERIODS.map((period) => {
          const selected = selectedPeriods.includes(period);
          const isOnlySelection = selected && selectedPeriods.length === 1;
          return (
            <button
              key={period}
              type="button"
              aria-pressed={selected}
              disabled={isOnlySelection}
              title={isOnlySelection ? "At least one period must remain selected" : undefined}
              onClick={() => togglePeriod(period)}
              className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                selected
                  ? "border-indigo-200 bg-indigo-50 font-medium text-indigo-700"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              } ${isOnlySelection ? "cursor-not-allowed" : ""}`}
            >
              {PERIOD_LABELS[period]}
            </button>
          );
        })}
      </div>
      <div className="mt-1.5 text-[11px] text-gray-500">
        Select one or more periods. They will appear in this order.
      </div>
    </div>
  );
};

const PeriodComparisonSettings = ({
  config,
  reportLevel = "customers",
  fieldOptions = [],
  errors = [],
  showMeasurePlacement = false,
  onChange,
}) => {
  const normalized = normalizePeriodComparisonConfig(config, reportLevel);
  const periodAxisFields = getPeriodComparisonAxisFields(reportLevel);
  if (!isLegacyPeriodComparisonConfig(normalized)) {
    const periodErrors = errors.filter((error) =>
      String(error?.field || "").startsWith("periodComparison")
    );
    return (
      <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50/70 p-3">
        <div className="grid grid-cols-1 gap-2 text-xs text-gray-600 md:grid-cols-2">
          <div className="rounded border border-gray-200 bg-white px-3 py-2">
            <label
              htmlFor="period-comparison-axis"
              className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-400"
            >
              Period basis
            </label>
            <ModernTimeDropdown
              id="period-comparison-axis"
              ariaLabel="Period basis"
              width="w-full"
              height="h-8"
              value={
                periodAxisFields.find(
                  (option) => option.value === normalized.axisField
                )?.label || periodAxisFields[0].label
              }
              options={periodAxisFields.map((option) => option.label)}
              onChange={(event) => {
                const selected = periodAxisFields.find(
                  (option) => option.label === event.target.value
                );
                if (selected) onChange({ ...normalized, axisField: selected.value });
              }}
            />
          </div>
          <div className="rounded border border-gray-200 bg-white px-3 py-2">
            <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Timezone
            </div>
            <div className="mt-1 font-medium text-gray-700">
              {PERIOD_COMPARISON_TIMEZONE}
            </div>
          </div>
        </div>
        <PeriodSelector
          periods={normalized.periods}
          onChange={(periods) => onChange({ ...normalized, periods })}
        />
        <div className="text-[11px] text-gray-500">
          Add optional grouping parameters, place them on Rows or Columns, and configure values below. Each metric is compared across the selected periods.
        </div>
        {showMeasurePlacement ? (
          <div className="rounded border border-gray-200 bg-white px-3 py-2">
            <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Display measures as
            </div>
            <div
              className="inline-flex overflow-hidden rounded-md border border-gray-200"
              role="group"
              aria-label="Display period comparison measures as"
            >
              {PERIOD_COMPARISON_MEASURE_PLACEMENTS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={normalized.measurePlacement === option.value}
                  onClick={() =>
                    onChange({ ...normalized, measurePlacement: option.value })
                  }
                  className={`px-3 py-1.5 text-xs ${
                    normalized.measurePlacement === option.value
                      ? "bg-indigo-50 text-indigo-700"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {periodErrors.length ? (
          <div className="space-y-1 text-xs text-red-600">
            {[...new Set(periodErrors.map((error) => error.message))].map((message) => (
              <div key={message}>{message}</div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }
  const comparisonOption =
    PERIOD_COMPARISON_TYPES.find(
      (option) => option.value === normalized.comparisonType
    ) || null;
  const hasFieldTypeMetadata = fieldOptions.some((option) => Boolean(option?.dataType));
  const customModuleFields = fieldOptions.filter((option) => {
    if (!String(option?.value || "").startsWith("custom_module.")) return false;
    if (!hasFieldTypeMetadata) return true;
    if (!option?.dataType) return false;
    return [
      "integer",
      "int",
      "bigint",
      "smallint",
      "number",
      "decimal",
      "float",
      "double",
      "real",
      "numeric",
    ].includes(String(option.dataType).toLowerCase());
  });
  const customModuleFieldChoices = customModuleFields.map((option) => ({
    value: option.value,
    display: `${option.label} — ${option.value}`,
  }));
  const comparisonLabels = PERIOD_COMPARISON_TYPES.map((option) => option.label);
  const updateArea = (index, patch) => {
    const areas = normalized.areas.map((area, areaIndex) =>
      areaIndex === index ? { ...area, ...patch } : area
    );
    onChange({ ...normalized, areas });
  };
  const addArea = (polarity) => {
    if (normalized.areas.length >= MAX_PERIOD_COMPARISON_AREAS) return;
    onChange({
      ...normalized,
      areas: [
        ...normalized.areas,
        { id: createAreaId(), label: "", field: "", polarity },
      ],
    });
  };
  const removeArea = (index) => {
    onChange({
      ...normalized,
      areas: normalized.areas.filter((_, areaIndex) => areaIndex !== index),
    });
  };
  const getAreaErrors = (index) =>
    errors.filter((error) =>
      String(error?.field || "").startsWith(`periodComparison.areas.${index}`)
    );
  const generalErrors = errors.filter((error) => {
    const field = String(error?.field || "");
    return field.startsWith("periodComparison") && !field.match(/^periodComparison\.areas\.\d+/);
  });
  const defaultPolarity =
    normalized.comparisonType.startsWith("employee_negative_") ? "negative" : "positive";
  const isTeamComparison = normalized.comparisonType === "team_areas";

  return (
    <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50/70 p-3">
      <div>
        <label
          htmlFor="period-comparison-view"
          className="mb-1 block text-xs text-gray-500"
        >
          Comparison view
        </label>
        <ModernTimeDropdown
          id="period-comparison-view"
          ariaLabel="Comparison view"
          width="w-full"
          height="h-8"
          value={comparisonOption?.label || ""}
          options={comparisonLabels}
          onChange={(event) => {
            const selected = PERIOD_COMPARISON_TYPES.find(
              (option) => option.label === event.target.value
            );
            if (selected) {
              const fixedPolarity = selected.value.startsWith("employee_negative_")
                ? "negative"
                : selected.value.startsWith("employee_positive_")
                  ? "positive"
                  : null;
              onChange({
                ...normalized,
                comparisonType: selected.value,
                areas: fixedPolarity
                  ? normalized.areas.map((area) => ({ ...area, polarity: fixedPolarity }))
                  : normalized.areas,
              });
            }
          }}
        />
      </div>

      <div className="grid grid-cols-1 gap-2 text-xs text-gray-600 md:grid-cols-3">
        <div className="rounded border border-gray-200 bg-white px-3 py-2">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Period basis
          </div>
          <div className="mt-1 font-medium text-gray-700">CRM Lead Created At</div>
        </div>
        <div className="rounded border border-gray-200 bg-white px-3 py-2">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Employee
          </div>
          <div className="mt-1 font-medium text-gray-700">
            {PERIOD_COMPARISON_EMPLOYEE_FIELD}
          </div>
        </div>
        <div className="rounded border border-gray-200 bg-white px-3 py-2">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Timezone
          </div>
          <div className="mt-1 font-medium text-gray-700">{PERIOD_COMPARISON_TIMEZONE}</div>
        </div>
      </div>

      <PeriodSelector
        periods={normalized.periods}
        onChange={(periods) => onChange({ ...normalized, periods })}
      />

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-xs font-medium text-gray-700">
              Score areas ({normalized.areas.length}/{MAX_PERIOD_COMPARISON_AREAS})
            </div>
            <div className="mt-0.5 text-[11px] text-gray-500">
              Select numeric Custom Module fields. Team overview needs at least one positive and one negative area.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {normalized.comparisonType === "team_areas" ? (
              <>
                <button
                  type="button"
                  disabled={normalized.areas.length >= MAX_PERIOD_COMPARISON_AREAS}
                  onClick={() => addArea("positive")}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" /> Positive area
                </button>
                <button
                  type="button"
                  disabled={normalized.areas.length >= MAX_PERIOD_COMPARISON_AREAS}
                  onClick={() => addArea("negative")}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" /> Negative area
                </button>
              </>
            ) : (
              <button
                type="button"
                disabled={normalized.areas.length >= MAX_PERIOD_COMPARISON_AREAS}
                onClick={() => addArea(defaultPolarity)}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Plus className="h-3.5 w-3.5" /> Add area
              </button>
            )}
          </div>
        </div>

        {normalized.areas.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 bg-white px-4 py-5 text-center text-xs text-gray-500">
            Add an area, name it, and map it to a numeric Custom Module field.
          </div>
        ) : (
          <div className="space-y-2">
            {normalized.areas.map((area, index) => {
              const areaErrors = getAreaErrors(index);
              const inputIdPrefix = `period-area-${area.id || index}`;
              return (
                <div
                  key={area.id || `period-area-${index}`}
                  className="rounded-md border border-gray-200 bg-white p-3"
                >
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_9rem_auto]">
                    <div>
                      <label
                        htmlFor={`${inputIdPrefix}-label`}
                        className="mb-1 block text-[11px] text-gray-500"
                      >
                        Area label
                      </label>
                      <input
                        id={`${inputIdPrefix}-label`}
                        type="text"
                        maxLength={100}
                        value={area.label}
                        onChange={(event) => updateArea(index, { label: event.target.value })}
                        placeholder={`Area ${index + 1}`}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-xs"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`${inputIdPrefix}-field`}
                        className="mb-1 block text-[11px] text-gray-500"
                      >
                        Custom Module field
                      </label>
                      <ModernTimeDropdown
                        id={`${inputIdPrefix}-field`}
                        ariaLabel={`Custom Module field for area ${index + 1}`}
                        width="w-full"
                        height="h-8"
                        placeholder="Select a numeric field"
                        value={
                          customModuleFieldChoices.find((choice) => choice.value === area.field)
                            ?.display || (area.field ? `${area.field} (unavailable)` : "")
                        }
                        options={customModuleFieldChoices.map((choice) => choice.display)}
                        onChange={(event) => {
                          const selected = customModuleFieldChoices.find(
                            (choice) => choice.display === event.target.value
                          );
                          if (selected) updateArea(index, { field: selected.value });
                        }}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`${inputIdPrefix}-polarity`}
                        className="mb-1 block text-[11px] text-gray-500"
                      >
                        Polarity
                      </label>
                      <ModernTimeDropdown
                        id={`${inputIdPrefix}-polarity`}
                        ariaLabel={`Polarity for area ${index + 1}`}
                        width="w-full"
                        height="h-8"
                        value={area.polarity === "negative" ? "Negative" : "Positive"}
                        options={["Positive", "Negative"]}
                        isDisabled={!isTeamComparison}
                        onChange={(event) =>
                          updateArea(index, {
                            polarity: String(event.target.value || "").toLowerCase(),
                          })
                        }
                      />
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove area ${index + 1}`}
                      onClick={() => removeArea(index)}
                      className="self-end rounded p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {areaErrors.length ? (
                    <div className="mt-2 space-y-1 text-[11px] text-red-600">
                      {[...new Set(areaErrors.map((error) => error.message))].map((message) => (
                        <div key={message}>{message}</div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {generalErrors.length ? (
        <div className="space-y-1 text-xs text-red-600">
          {[...new Set(generalErrors.map((error) => error.message))].map((message) => (
            <div key={message}>{message}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default PeriodComparisonSettings;
